import { useCallback } from 'react';
import { SettingType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { MessagingSettingsV1 } from '@frontend/api-messaging-settings';
import { useAIReceptionistSettings } from '@frontend/ai-receptionist-notification-settings';
import { getUser } from '@frontend/auth-helpers';
import { useMessageAction } from '@frontend/contact-actions-send-message';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import {
  getThreadAiNotificationClassification,
  notificationSounds,
  useAiReceptionistStateNotification,
} from '@frontend/notifications';
import { useAppScopeStore } from '@frontend/scope';
import { useMessagingAIRCustomizationFlag } from '@frontend/shared';
import { GetWeavePopNotificationByType } from '@frontend/types';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

type ThreadAiActivity = NonNullable<
  NonNullable<GetWeavePopNotificationByType<'ai-receptionist-state'>['payload']>['threadAiActivity']
>;

const getThreadAiDisplayName = (params: {
  person: ThreadAiActivity['person'];
  personPhoneNumber: ThreadAiActivity['personPhoneNumber'];
}) => {
  const person = params.person;
  const preferredName = person?.preferredName || '';

  if (preferredName) {
    return preferredName;
  }

  const firstName = person?.firstName || '';
  const lastName = person?.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();

  if (fullName) {
    return fullName;
  }

  return params.personPhoneNumber || '';
};

export const useAIReceptionistStateWSHandler = () => {
  const user = getUser();
  const { onClick: onView } = useMessageAction();

  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const checkUserLocationPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess(selectedLocationIds);
  const { isActive: isAIReceptionistNotificationEnabled } = useMessagingAIRCustomizationFlag(selectedLocationIds);
  const { getLocationAIReceptionistSettings } = useAIReceptionistSettings();

  const messagingUserSettingsQuery = MessagingSettingsV1.Queries.useGetQuery(
    {
      settingType: SettingType_Enum.INBOX_USER_SETTING,
      userId: user?.userID ?? '',
      orgId: selectedOrgId,
    },
    {
      enabled: isAIReceptionistNotificationEnabled && !!user?.userID,
      select: (data) => {
        if ('inboxUserSetting' in data.setting) {
          return data.setting.inboxUserSetting;
        }
        return undefined;
      },
    }
  );

  const { create: createNotification } = useAiReceptionistStateNotification({
    onView: (notification) => {
      const activity = notification.payload.threadAiActivity;

      if (!activity) {
        return;
      }

      onView({
        threadId: activity.threadId ?? '',
        threadGroupId: activity.locationId ?? '',
        personPhone: activity.personPhoneNumber,
        locationPhone: activity.locationPhoneNumber,
        groupIds: [activity.locationId ?? ''],
      });
    },
  });

  const wsEventHandler = useCallback<GetWebsocketEventHandler<NotificationType.AI_RECEPTIONIST_STATE>>(
    async (event) => {
      if (!isAIReceptionistNotificationEnabled) {
        return;
      }

      const { params } = event;

      const threadAiActivity = params.threadAiActivity;

      if (!threadAiActivity) {
        return;
      }

      const locationId = threadAiActivity.locationId ?? '';
      const notificationClassification = getThreadAiNotificationClassification(threadAiActivity);

      if (!notificationClassification) {
        return;
      }

      const weaveNumber = threadAiActivity.locationPhoneNumber;

      // If User does not have access to location phone number in the event's thread, ignore the event
      if (!!weaveNumber && !checkUserLocationPhoneAccess(weaveNumber)) {
        return;
      }

      const locationSettings = getLocationAIReceptionistSettings(locationId);
      const shouldNotifyByCategory =
        notificationClassification.category === 'needs-attention'
          ? !!locationSettings.enableNeedsAttentionNotifications
          : !!locationSettings.enableSessionCompleteNotifications;

      const soundId =
        notificationSounds.find(
          (sound) => sound.name.toLowerCase() === messagingUserSettingsQuery.data?.notificationSound.toLowerCase()
        )?.id ?? '';

      if (shouldNotifyByCategory) {
        const displayName = getThreadAiDisplayName({
          person: threadAiActivity.person,
          personPhoneNumber: threadAiActivity.personPhoneNumber,
        });

        createNotification(
          {
            id: String(event.id),
            type: 'ai-receptionist-state',
            payload: params,
            timestamp: Date.now(),
            state: {
              paused: false,
              status: 'unread',
            },
          } satisfies GetWeavePopNotificationByType<'ai-receptionist-state'>,
          {
            force: true,
            soundId,
            os: {
              title: 'Messaging Event by AI Receptionist',
              body: `${notificationClassification.label}\n${displayName}`,
            },
          }
        );
      }
    },
    [
      checkUserLocationPhoneAccess,
      createNotification,
      getLocationAIReceptionistSettings,
      isAIReceptionistNotificationEnabled,
      messagingUserSettingsQuery.data?.notificationSound,
      onView,
    ]
  );

  return wsEventHandler;
};

export const useAIReceptionistStateWSSubscription = () => {
  const handler = useAIReceptionistStateWSHandler();
  return useWebsocketEventSubscription(NotificationType.AI_RECEPTIONIST_STATE, handler);
};
